import axios from 'axios';
import { API_BASE_URL, ContentTypes, getAxiosRequestConfig } from './apiBase';
import IAttributeDto from '../models/iAttributeDto';
import IFilterValue from '../models/iFilterValue';
import IIndexValue from '../models/iIndexValue';
import {
  IProductSearchRequest,
  IFeedbackRequest,
  ICustomerRequestResponse,
  IProductSearchResponse,
  SetonPipeMarkerProcessingResponse,
  IProductSummaryResponse,
} from '../models/iProductSearchRequest';
import IFacetValue from '../models/iFacetValue';
import { ICustomRanking } from '../models/iCustomRanking';
import { ICustomRankingCreate, ICustomRankingUpdate } from '../models/iCustomRankingDto';
import { EnabledProductTypes, defaultEnabledProductTypes } from '../types/ProductTypeFilter';
import ISalesOrg from '../models/iSalesOrg';
import { IEto, ICreateEto, IUpdateEto } from '../models/iEto';

const baseUrl = API_BASE_URL + '/ProductSearch';

/**
 * Gets all products that match the applied filters
 * @param bearerToken
 * @param filterValues
 * @param indexNames
 * @param signal
 * @param options
 * @returns
 */
export const searchProducts = async (
  bearerToken: string,
  filterValues: IFilterValue[],
  indexNames: string[],
  signal: AbortSignal,
  options: {
    salesOrgDivs?: number[];
    isExact?: boolean;
    enabledProductTypes?: EnabledProductTypes;
    correlationId?: string | null;
  } = {}
): Promise<IProductSearchResponse> => {
  const { salesOrgDivs, isExact = true, enabledProductTypes = defaultEnabledProductTypes, correlationId } = options;
  const request: IProductSearchRequest = {
    appliedFilters: filterValues,
    newFacetAttributeName: '',
    indexNames,
    salesOrgDivs,
    enabledProductTypes,
  };
  if (correlationId) {
    request.correlationId = correlationId;
  }
  const config = {
    ...getAxiosRequestConfig(bearerToken),
    signal,
  };
  const response = await axios.post<IProductSearchResponse>(`${baseUrl}/search?isExact=${isExact}`, request, config);
  return response.data;
};

/**
 * Get a list of all facet values for the selected facetable attribute
 * @param bearerToken
 * @param currentFilterValues
 * @param newFacetAttributeName
 * @param IndexNames
 * @param salesOrgDivs
 * @param enabledProductTypes
 * @returns
 */
export const getFacetValues = async (
  bearerToken: string,
  currentFilterValues: IFilterValue[],
  newFacetAttributeName: string,
  IndexNames: string[],
  salesOrgDivs?: number[],
  enabledProductTypes: EnabledProductTypes = defaultEnabledProductTypes
): Promise<IFacetValue[]> => {
  const body: IProductSearchRequest = {
    newFacetAttributeName: newFacetAttributeName,
    appliedFilters: currentFilterValues,
    indexNames: IndexNames,
    enabledProductTypes: enabledProductTypes,
  };
  if (salesOrgDivs) {
    body.salesOrgDivs = salesOrgDivs;
  }
  const response = await axios.post<IFacetValue[]>(`${baseUrl}/facets`, body, getAxiosRequestConfig(bearerToken));
  return response.data;
};

/**
 * Get a list of all attributes for the selected indexes
 * @param bearerToken
 * @param IndexNames
 * @returns
 */
export const getAttributes = async (bearerToken: string, IndexNames: string[]): Promise<IAttributeDto[]> => {
  const queryParams = IndexNames.map((name) => `indexNames=${encodeURIComponent(name)}`).join('&');

  const response = await axios.get<IAttributeDto[]>(
    `${baseUrl}/attributes?${queryParams}`,
    getAxiosRequestConfig(bearerToken)
  );

  return response.data;
};

/**
 * Get a list of all indexes with a display name
 * @param bearerToken
 * @returns
 */
export const getIndexes = async (bearerToken: string): Promise<IIndexValue[]> => {
  const response = await axios.get<IIndexValue[]>(`${baseUrl}/indexes`, getAxiosRequestConfig(bearerToken));
  return response.data;
};

/**
 * Get a list of all sales organizations
 * @param bearerToken
 * @returns
 */
export const getSalesOrgs = async (bearerToken: string): Promise<ISalesOrg[]> => {
  const response = await axios.get<ISalesOrg[]>(`${baseUrl}/sales-orgs`, getAxiosRequestConfig(bearerToken));
  return response.data;
};

/**
 *
 * @param bearerToken
 * @param customerQuery
 * @param files
 * @returns
 */
export const customerRequest = async (
  bearerToken: string,
  customerQuery: string,
  indexName: string,
  salesOrgDivs?: number[],
  files?: File[]
): Promise<ICustomerRequestResponse> => {
  const formData = new FormData();
  if (salesOrgDivs) {
    salesOrgDivs.forEach((org) => {
      formData.append('salesOrgDivs', org.toString());
    });
  }
  formData.append('text', customerQuery);
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }
  const response = await axios.post<ICustomerRequestResponse>(
    `${baseUrl}/customer-request/${indexName}`,
    formData,
    getAxiosRequestConfig(bearerToken, {}, ContentTypes.FormData)
  );
  return response.data;
};

/**
 * Get a generative summary and URLs for a customer request using Gemini AI
 * @param bearerToken
 * @param customerQuery
 * @param indexName
 * @param files
 * @returns
 */
export const getGenerativeSummary = async (
  bearerToken: string,
  customerQuery: string,
  indexName: string,
  files?: File[]
): Promise<IProductSummaryResponse> => {
  const formData = new FormData();
  formData.append('text', customerQuery);
  
  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append('files', file);
    });
  }
  
  const response = await axios.post<IProductSummaryResponse>(
    `${baseUrl}/generative-summary/${indexName}`,
    formData,
    getAxiosRequestConfig(bearerToken, {}, ContentTypes.FormData)
  );
  return response.data;
};

/**
 * Get a list of all custom rankings
 * @param bearerToken
 */
export const getCustomRankings = async (bearerToken: string): Promise<ICustomRanking[]> => {
  const response = await axios.get<ICustomRanking[]>(`${baseUrl}/custom-rankings`, getAxiosRequestConfig(bearerToken));
  return response.data;
};

/**
 * Create a new custom ranking
 * @param bearerToken
 * @param customRanking
 */
export const createCustomRanking = async (
  bearerToken: string,
  customRanking: ICustomRankingCreate
): Promise<ICustomRanking> => {
  const response = await axios.post<ICustomRanking>(
    `${baseUrl}/custom-rankings`,
    customRanking,
    getAxiosRequestConfig(bearerToken)
  );
  return response.data;
};

/**
 * Delete a custom ranking by ID
 * @param bearerToken
 * @param id
 */
export const deleteCustomRanking = async (bearerToken: string, id: ICustomRanking['id']): Promise<void> => {
  await axios.delete(`${baseUrl}/custom-rankings/${id}`, getAxiosRequestConfig(bearerToken));
};

/**
 * Update a custom ranking by ID
 * @param bearerToken
 * @param id
 * @param updateData
 */
export const updateCustomRanking = async (
  bearerToken: string,
  id: ICustomRanking['id'],
  updateData: ICustomRankingUpdate
): Promise<ICustomRanking> => {
  const response = await axios.put<ICustomRanking>(
    `${baseUrl}/custom-rankings/${id}`,
    updateData,
    getAxiosRequestConfig(bearerToken)
  );
  return response.data;
};

/**
 * Gets all ETO records.
 * @param bearerToken The authentication token.
 */
export const getAllEtos = async (bearerToken: string): Promise<IEto[]> => {
  const response = await axios.get<IEto[]>(`${baseUrl}/etos`, getAxiosRequestConfig(bearerToken));
  return response.data;
};

/**
 * Creates a new ETO record.
 * @param bearerToken The authentication token.
 * @param etoData The data for the new ETO.
 */
export const createEto = async (bearerToken: string, etoData: ICreateEto): Promise<IEto> => {
  const response = await axios.post<IEto>(`${baseUrl}/etos`, etoData, getAxiosRequestConfig(bearerToken));
  return response.data;
};

/**
 * Deletes an ETO record.
 * @param bearerToken The authentication token.
 * @param id The ID of the ETO mapping to delete.
 */
export const deleteEto = async (bearerToken: string, id: number): Promise<void> => {
  await axios.delete(`${baseUrl}/etos/${id}`, getAxiosRequestConfig(bearerToken));
};

/**
 * Updates an ETO record.
 * @param bearerToken The authentication token.
 * @param id The ID of the ETO record to update.
 * @param etoData The updated data for the ETO.
 */
export const updateEto = async (bearerToken: string, etoId: number, etoData: IUpdateEto): Promise<IEto> => {
  const response = await axios.put<IEto>(`${baseUrl}/etos/${etoId}`, etoData, getAxiosRequestConfig(bearerToken));
  return response.data;
};

/**
 * Submit search feedback
 * @param bearerToken
 * @param feedback
 */
export const submitSearchFeedback = async (bearerToken: string, feedback: IFeedbackRequest): Promise<void> => {
  await axios.post(`${baseUrl}/feedback`, feedback, getAxiosRequestConfig(bearerToken));
};

/**
 * Process Seton Pipe Markers Excel file
 * @param bearerToken
 * @param excelFile
 */
export const processSetonPipeMarkersFile = async (
  bearerToken: string,
  excelFile: File
): Promise<SetonPipeMarkerProcessingResponse> => {
  const formData = new FormData();
  formData.append('ExcelFile', excelFile);

  const response = await axios.post<SetonPipeMarkerProcessingResponse>(`${baseUrl}/seton-pipe-markers`, formData, {
    ...getAxiosRequestConfig(bearerToken),
    headers: {
      ...getAxiosRequestConfig(bearerToken).headers,
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};
